/**
 * ModalManager
 *
 * A small, reusable helper for driving Bootstrap-styled modals that live
 * *inside* a Shadow DOM. Bootstrap's own `Modal` JS relocates the backdrop to
 * `document.body` and manages focus at the light-DOM level, which does not work
 * reliably across a shadow boundary. This controller instead keeps the backdrop
 * inside the shadow root and toggles the standard `.show` / display state
 * manually, while still honouring the familiar Bootstrap data attributes:
 *
 *   - `[data-bs-dismiss="modal"]` / `[data-dismiss]` — clickable dismiss targets
 *   - `data-bs-backdrop="static"`                    — clicking the backdrop won't close
 *   - `data-bs-keyboard="false"`                     — Escape won't close
 *
 * Usage:
 *   private modals = new ModalManager(this.shadow);
 *   // during init, once per modal element:
 *   this.modals.register(myModalEl, { onHide: () => this.resetForm() });
 *   // to open/close:
 *   this.modals.show(myModalEl);
 *   this.modals.hide(myModalEl);
 *   // during disconnectedCallback:
 *   this.modals.destroy();
 */
export interface ModalRegisterOptions {
    /** Invoked after the modal is hidden (state cleanup, form resets, etc.). */
    onHide?: (modal: HTMLElement) => void;
    /** Invoked after the modal is shown (autofocus, etc.). */
    onShow?: (modal: HTMLElement) => void;
}

export class ModalManager {
    private readonly shadow: ShadowRoot;
    private backdropEl: HTMLElement | null = null;
    private openModalEl: HTMLElement | null = null;
    private readonly onHideMap = new WeakMap<HTMLElement, (modal: HTMLElement) => void>();
    private readonly onShowMap = new WeakMap<HTMLElement, (modal: HTMLElement) => void>();

    constructor(shadow: ShadowRoot) {
        this.shadow = shadow;
        document.addEventListener('keydown', this.onKeydown);
    }

    /** The modal currently open, if any. */
    get current(): HTMLElement | null {
        return this.openModalEl;
    }

    /**
     * Wire dismissal behaviour for a modal: any `[data-bs-dismiss="modal"]` /
     * `[data-dismiss]` element closes it, and clicking the backdrop closes it
     * unless the modal opts out with `data-bs-backdrop="static"`.
     */
    register(modal: HTMLElement, options: ModalRegisterOptions = {}): void {
        if (options.onHide) this.onHideMap.set(modal, options.onHide);
        if (options.onShow) this.onShowMap.set(modal, options.onShow);

        modal.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-bs-dismiss="modal"], [data-dismiss]')) {
                this.hide(modal);
                return;
            }
            if (target === modal && modal.getAttribute('data-bs-backdrop') !== 'static') {
                this.hide(modal);
            }
        });
    }

    /** Show a modal, closing any other modal that is currently open first. */
    show(modal: HTMLElement): void {
        if (this.openModalEl && this.openModalEl !== modal) this.hide(this.openModalEl);

        if (!this.backdropEl) {
            this.backdropEl = document.createElement('div');
            this.backdropEl.className = 'modal-backdrop fade';
            this.shadow.appendChild(this.backdropEl);
        }

        modal.style.display = 'block';
        modal.removeAttribute('aria-hidden');
        modal.setAttribute('aria-modal', 'true');

        // Force a reflow so the `.show` fade transition actually plays.
        void modal.offsetWidth;
        modal.classList.add('show');
        this.backdropEl.classList.add('show');
        this.openModalEl = modal;

        this.onShowMap.get(modal)?.(modal);
    }

    /** Hide a specific modal. No-op if it is not currently shown. */
    hide(modal: HTMLElement): void {
        if (!modal.classList.contains('show')) return;

        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
        modal.removeAttribute('aria-modal');
        modal.style.display = 'none';

        this.backdropEl?.remove();
        this.backdropEl = null;
        if (this.openModalEl === modal) this.openModalEl = null;

        this.onHideMap.get(modal)?.(modal);
    }

    /** Hide whichever modal is currently open, if any. */
    hideCurrent(): void {
        if (this.openModalEl) this.hide(this.openModalEl);
    }

    /** Remove listeners and backdrop. Call from `disconnectedCallback`. */
    destroy(): void {
        document.removeEventListener('keydown', this.onKeydown);
        this.backdropEl?.remove();
        this.backdropEl = null;
        this.openModalEl = null;
    }

    private readonly onKeydown = (e: KeyboardEvent): void => {
        if (e.key !== 'Escape' || !this.openModalEl) return;
        if (this.openModalEl.getAttribute('data-bs-keyboard') === 'false') return;
        this.hide(this.openModalEl);
    };
}
